/**
 * Módulo del tutorial (grupo "Para producir mejor"): Las utilidades del campo (KAN-232).
 *
 * Herramientas del día a día: Agenda (eventos del campo), Recordatorios
 * (vacunas y tratamientos que te avisan, con práctica real), Notas (públicas
 * para todo el campo o privadas, con el control del dueño: si alguien edita
 * tu nota pública, siempre podés volver a tu versión) y las dos calculadoras
 * — Engorde con sus CUATRO tabs (Calculadora, Histórico de costos reales,
 * Precios de mercado, Corrales con engorde real) y Ración (materia seca,
 * PB/EM, GDP estimado y el puente que las conecta: "Usar mi ración").
 *
 * Anclas: menu-herramientas, tiles data-testid (herramientas-tab-*),
 * recordatorios-form-titulo/btn-guardar, notas-check-publico,
 * notas-btn-guardar, engorde-tab-{calculadora,historico,precios,corrales},
 * racion-tus-alimentos, racion-btn-ir-engorde.
 */

import type { TutorialModuleDef } from '../../../services/tutorial/types';

export const utilidadesModule: TutorialModuleDef = {
  id: 'utilidades',
  title: 'Las utilidades del campo',
  description: 'Agenda, recordatorios y notas (públicas o privadas) — y las calculadoras de engorde y ración explicadas de punta a punta, con cómo se conectan.',
  emoji: '🧮',
  image: 'images/tutorial/utilidades.png',
  permission: { module: 'tools', action: 'view' },
  grupo: 'produccion',
  route: '/herramientas',
  steps: [
    {
      id: 'concepto-utilidades',
      route: '/tutorial',
      text: 'El cuchillo canero de la app: agenda, recordatorios, notas y dos calculadoras que responden preguntas de todos los días.',
      narration:
        'Ahora unas cositas que usás seguido, como el cuchillo canero: una agenda para el campo, recordatorios que no se te pasan, notas al vuelo, y dos calculadoras — una para saber cómo viene el engorde, y otra para armar la ración del corral. Cosas de todos los días, todas en el mismo lugar.',
    },
    {
      id: 'ir-a-herramientas',
      target: '[data-ar-guide="menu-herramientas"]',
      label: 'Herramientas (menú)',
      text: 'En el menú, tocá "Herramientas" (en celular: menú ☰). Ahí viven las cinco utilidades.',
      narration:
        'Andá al menú y tocá Herramientas. Ahí están las cinco, a la vista: Agenda, Engorde, Ración, Notas y Recordatorios. Vamos de a una, que todas se pagan.',
    },
    {
      id: 'agenda',
      route: '/herramientas?app=agenda',
      target: '[data-ar-guide="herramientas-tab-agenda"]',
      label: '📅 Agenda',
      advanceOnClick: false,
      text: 'La Agenda del campo: vacunaciones, servicios, embarques — todo con su fecha, para que nada se te cruce.',
      narration:
        'Primero la Agenda: el calendario del campo. Vacunación, inicio de servicio, el embarque del viernes: todo con su fecha, anotado donde no se pierde. La cabeza para producir, la agenda para acordarse.',
    },
    {
      id: 'recordatorios',
      route: '/herramientas?app=recordatorios',
      target: '[data-ar-guide="herramientas-tab-recordatorios"]',
      label: '🔔 Recordatorios',
      advanceOnClick: false,
      text: '"Recordatorios" es la Agenda con alarma: vacunas y tratamientos por animal o lote, con fecha y hora — la app te avisa. Probalo: el formulario de alta está abajo.',
      narration:
        'Y si querés que la app te toque el hombro, están los Recordatorios: vacunas, tratamientos, vueltas de control — por animal o por lote, con fecha y hora. Llegado el día, te avisa. Chau con acordarse por la mañana y olvidarse al mediodía. Y para que lo veas, en un toque más cargamos uno de verdad.',
    },
    {
      id: 'recordatorio-titulo',
      route: '/herramientas?app=recordatorios',
      target: '[data-ar-guide="recordatorios-form-titulo"]',
      label: 'Título del recordatorio',
      advanceOnClick: false,
      text: 'En el formulario de abajo, poni el título: "Vacunar lote de arriba", por ejemplo. Después le sumás fecha, hora y a qué animal o lote.',
      narration:
        'Bajá al formulario que dice Agregar Nuevo Recordatorio, y arrancá por el título: Vacunar el lote de arriba, lo que sea. Debajo le ponés la fecha, la hora, y si es para un animal puntual o el lote entero.',
    },
    {
      id: 'recordatorio-guardar',
      route: '/herramientas?app=recordatorios',
      target: '[data-ar-guide="recordatorios-btn-guardar"]',
      label: 'Agregar Recordatorio',
      text: 'Completá el resto a tu gusto y tocá "Agregar Recordatorio" — desde ese día, la app te avisa sola.',
      narration:
        'Completá fecha y el resto a tu gusto, y apretá Agregar Recordatorio. Quedó agendado: llegado el día, la app te toca el hombro. Vos ocupate del campo, que acordarse ya no es tu problema.',
    },
    {
      id: 'notas',
      route: '/herramientas?app=notas',
      target: '[data-ar-guide="herramientas-tab-notas"]',
      label: '📝 Notas',
      advanceOnClick: false,
      text: '"Notas" para lo que no cabe en ningún lado: el alambrado picado del fondo, la idea del mediodía, la lista del corralón. Con categorías para ordenarlas (veterinaria, alimentación, mantenimiento…).',
      narration:
        'Las Notas son el bolsillo de la campera: lo que no cabe en ningún lado, va acá. El alambrado picado del fondo, la idea que se te ocurrió al mediodía, la lista del corralón. Y traen categorías para no revolver el bolsillo: veterinaria, alimentación, reproducción, ventas, mantenimiento. Ahora, el detalle importante: quién las ve.',
    },
    {
      id: 'nota-publica-o-privada',
      route: '/herramientas?app=notas',
      target: '[data-ar-guide="notas-check-publico"]',
      label: 'Pública o privada',
      advanceOnClick: false,
      text: '"¿Quién la ve?" decide la visibilidad: 👥 PÚBLICA = todos los miembros del campo la leen (y pueden aportar). 🔒 PRIVADA = la ves SOLO vos. 💰 FINANZAS = vos y quienes tienen acceso a la parte financiera (gastos, pagos, precios). Vos elegís nota por nota.',
      narration:
        'Este selector es el que manda. Pública: todo el mundo de tu campo la va a ver, sirve para las cosas del equipo, el alambrado del fondo, el pedido de la veterinaria. Privada: la ves sólo vos. Y finanzas: la ven vos y quienes manejan la plata del campo, para los gastos, los pagos y los precios que no tiene que ver todo el mundo. Cada nota, su destino. Elegí vos, nota por nota.',
    },
    {
      id: 'nota-rollback',
      route: '/herramientas?app=notas',
      text: '¿Y si alguien modifica tu nota pública? SOS VOS el dueño: con el botón ↶ "Versión anterior" de la nota, la volvés a TU última versión en un toque. Control total sobre lo que escribiste.',
      narration:
        'Y el control del dueño, que es la tranquilidad de este sistema: una nota pública la puede aportar cualquiera del equipo con permiso, pero la nota es tuya. Si alguien le cambia algo que no te cierra, tocás el botón de la flechita para atrás, Volver a versión anterior, y la nota vuelve a tu última versión, tal como la escribiste vos. Nadie te pisa lo tuyo: el campo aporta, el dueño decide.',
    },
    {
      id: 'engorde-intro',
      route: '/herramientas?app=calculadora-engorde',
      target: '[data-ar-guide="herramientas-tab-engorde"]',
      label: '🐄 Calculadora de Engorde',
      advanceOnClick: false,
      text: 'La "Calculadora de Engorde" responde la pregunta del negocio: ¿conviene vender ahora o engordar un mes más? Tiene CUATRO tabs — te los recorro uno por uno.',
      narration:
        'Ahora una joya, y es seria: la Calculadora de Engorde. Responde la pregunta que te hacés cada año: vendo ahora o engordo un mes más. Y para eso tiene cuatro pestañas, que te las recorro una por una: la calculadora propiamente dicha, tus costos históricos, los precios del mercado, y el engorde real de tus corrales. A esa altura, decidís con números, no con ganas.',
    },
    {
      id: 'engorde-tab-calculadora',
      route: '/herramientas?app=calculadora-engorde',
      target: '[data-ar-guide="engorde-tab-calculadora"]',
      label: '📊 Tab Calculadora',
      advanceOnClick: false,
      text: '📊 CALCULADORA: categoría, peso de entrada y de salida, cantidad — y te da el beneficio neto, los días que falta, el margen y la ganancia por mes. Bonus: "Usar Costos Históricos" (tus gastos reales) y "Usar mi ración" (lo que sale tu dieta).',
      narration:
        'La primera pestaña es la calculadora. Le contás el negocio: qué categoría, con qué peso entran, a qué peso querés vender, cuántos animales. Y te contesta con el beneficio neto, cuántos días falta, el margen, y la ganancia por mes para comparar contra otras alternativas. Y tiene dos atajos de lujo: Usar Costos Históricos, que trae los gastos reales de tu campo; y Usar mi ración, que aplica lo que sale tu dieta de la otra calculadora. De esas dos hablamos en un rato.',
    },
    {
      id: 'engorde-tab-historico',
      route: '/herramientas?app=calculadora-engorde',
      target: '[data-ar-guide="engorde-tab-historico"]',
      label: '📈 Tab Histórico',
      advanceOnClick: false,
      text: '📈 HISTÓRICO: TUS costos reales de los últimos 12 meses — alimenticios, no alimenticios y fijos, con el GDP por corral. Podés verlos en pesos constantes, nominales o dólares.',
      narration:
        'Segunda pestaña: el Histórico. Acá no hay fantasía: son tus gastos reales de los últimos doce meses, separaditos en alimenticios, no alimenticios y fijos, con la ganancia por día de cada corral. Y para que la inflación no te mienta, podés verlos en pesos constantes, en pesos nominales, o directamente en dólares. Tu costo de producción real, sin maquillaje.',
    },
    {
      id: 'engorde-tab-precios',
      route: '/herramientas?app=calculadora-engorde',
      target: '[data-ar-guide="engorde-tab-precios"]',
      label: '📈 Tab Precios',
      advanceOnClick: false,
      text: '📈 PRECIOS: la evolución del mercado (Cañuelas, MAG) por categoría, últimos 12 meses — precio actual y variación anual. Para saber si el momento acompaña.',
      narration:
        'Tercera pestaña: los Precios del mercado. La evolución de los últimos doce meses por categoría, con el precio de hoy y la variación del año. Así, cuando la calculadora te dice engordá un mes más, mirás acá si el precio acompaña la espera. El pulso del mercado, sin llamar a nadie.',
    },
    {
      id: 'engorde-tab-corrales',
      route: '/herramientas?app=calculadora-engorde',
      target: '[data-ar-guide="engorde-tab-corrales"]',
      label: '📍 Tab Corrales',
      advanceOnClick: false,
      text: '📍 CORRALES: el engorde REAL de tu campo — GDP y costo por kilo ganado de cada corral, calculado con TUS pesajes y gastos. Con "Calcular engorde real" y el comparativo Estimado vs Real.',
      narration:
        'Y la cuarta es la más fina: Corrales. Tocá Calcular engorde real, y la app revisa tus pesajes y tus gastos, corral por corral, y te saca la ganancia por día real y el costo real de cada kilo ganado. Los animales con ganancias raras quedan afuera de la cuenta para no ensuciar. Y abajo te pone el comparativo: lo que estimabas contra lo que dio en verdad. Ahí sabés si la dieta, el manejo o los números hay que ajustarlos.',
    },
    {
      id: 'racion-intro',
      route: '/herramientas?app=calculadora-racion',
      target: '[data-ar-guide="herramientas-tab-racion"]',
      label: '🌾 Calculadora de Ración',
      advanceOnClick: false,
      text: 'La "Calculadora de Ración" arma la dieta: ingredientes con sus % o kilos — y te calcula materia seca, balance de proteína y energía, el GDP esperado y el costo por animal por día.',
      narration:
        'Y su hermana: la Calculadora de Ración. Acá armás la dieta del corral, ingrediente por ingrediente, en porcentaje o en kilos. Ella te calcula la materia seca que aporta, si la proteína y la energía cubren lo que el animal necesita, la ganancia por día esperada con esa dieta, y el costo por animal por día. La dieta bien contada, antes de llenar el comedero.',
    },
    {
      id: 'racion-ingredientes',
      route: '/herramientas?app=calculadora-racion',
      target: '[data-ar-guide="racion-tus-alimentos"]',
      label: '🌾 Tus alimentos',
      advanceOnClick: false,
      text: 'Los ingredientes son TUS alimentos del catálogo — cada uno trae el precio de tu ÚLTIMA COMPRA (y si no, lo cargás a mano y se recuerda). Sin alimentos cargados, te ofrece crearlos al toque.',
      narration:
        'Fijate en estos chips: son tus alimentos, los mismos del catálogo de conceptos. Y el precio no hay que adivinarlo: cada uno trae el precio de tu última compra, el que pagaste de verdad la última vez; y si preferís otro, lo escribís a mano y se acuerda para la próxima. ¿Todavía no cargaste ningún alimento? Te ofrece crear el primero ahí mismo, y después de eso, cada dieta sale con tus números.',
    },
    {
      id: 'racion-puente',
      route: '/herramientas?app=calculadora-racion',
      target: '[data-ar-guide="racion-btn-ir-engorde"]',
      label: 'El puente Ración → Engorde',
      advanceOnClick: false,
      text: 'Y LAS DOS SE CONECTAN: esta ración se guarda sola y alimenta la Calculadora de Engorde — con "Usar mi ración" aplica tu costo por kilo ganado y recalcula los meses. También podés "Asignar a corral" para el comparativo del tab Corrales.',
      narration:
        'Y ahora el puente, que es lo elegante: esta ración no queda huérfana. Se guarda sola, y la Calculadora de Engorde la espera: cuando allá tocás Usar mi ración, aplica el costo por kilo ganado de esta dieta, y con la ganancia por día recalcula los meses que falta, o directamente el peso de salida que vas a lograr. Y con Asignar a corral, vinculás esta mezcla a un corral puntual, para que el comparativo del tab Corrales te muestre estimado contra real. Dieta, engorde y corral: una sola conversación.',
    },
    {
      id: 'cierre-utilidades',
      route: '/herramientas',
      text: 'Eso es Herramientas: agenda, recordatorios, notas con su control — y dos calculadoras que trabajan juntas. Y de yapa, el buscador ⌘K te lleva a cualquiera en dos toques.',
      narration:
        'Eso es todo el cajón de herramientas: la agenda, los recordatorios que avisan solos, las notas públicas o privadas que nadie te pisa, y las dos calculadoras que se pasan la información entre ellas. Y un secreto de yapa: el buscador de arriba, con control y k, te lleva a cualquiera de estas en dos toques, sin caminar por el menú. Ahora sí, a producir ordenado, criollo.',
    },
  ],
  completionCheck: async () => false, // se completa haciendo el recorrido, no por datos
};
